import React, { useCallback, useState } from 'react';

import * as Sentry from '@sentry/browser';
import { useSnackbar } from 'notistack';
import pluralize from 'pluralize';
import { useParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAccommodationServiceFetch } from '~/components/Accommodation/hooks/useAccommodationServiceFetch';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { useGetInternalPropertyRooms } from '~/hooks/apis/accommodation/useGetInternalPropertyRooms';
import useToggleState from '~/hooks/useToggleState';

import {
  ExtendedMappedInternalRoom,
  InternalRoom,
  MappedInternalRoom,
  Room,
  RoomMappingPayload,
  RoomMappingResponse,
  RoomResponse,
  createAndMapRoom,
  deleteRoomMappings,
  getRoomMappings,
  listRooms,
  postRoomMappings,
  verifyMappedRooms,
} from '~/services/AccommodationService';

import { titleCase } from '~/utils/stringUtils';

import MappedRooms from './MappedRooms';
import UnmappedRooms from './UnmappedRooms';

interface RoomWithPackage extends Room {
  hasActiveLpcPackage?: boolean;
}

function AccommodationPropertyRoomMappingSection() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [internalRooms, setInternalRooms] = useState<Array<InternalRoom>>([]);
  const [propertyRooms, setPropertyRooms] = useState<Array<RoomWithPackage>>([]);

  const [updatePayload, setUpdatePayload] = useState<RoomMappingPayload>([]);
  const [removePayload, setRemovePayload] = useState<RoomMappingPayload>([]);

  const [selectedControlRoom, setSelectedControlRoom] = useState<RoomWithPackage>();
  const [selectedInternalRooms, setSelectedInternalRooms] = useState<Set<string>>(new Set()); // Set of internalRoomId

  const [alreadyMappedRooms, setAlreadyMappedRooms] = useState<Record<string, boolean>>({});
  const [appliedButNotSavedRooms, setAppliedButNotSavedRooms] = useState<Array<InternalRoom>>([]);

  const [verifiedRoomMappingIds, setVerifiedRoomMappingIds] = useState<Set<string>>(new Set());
  const [verificationComments, setVerificationComments] = useState('');

  const [internalPropertyFilter, setInternalPropertyFilter] = useState(''); // internalProperty.id, '' for all

  const {
    data: internalPropertyRooms = [],
    isLoading: isInternalPropertyRoomsLoading,
    isError: isInternalPropertyRoomsError,
  } = useGetInternalPropertyRooms({ propertyId });

  const showConfirmDialog = useConfirmDialog();
  const {
    isToggled: isVerificationCommentsModalOpen,
    toggleOn: setOpenVerificationCommentsModal,
    toggleOff: setCloseVerificationCommentsModal,
  } = useToggleState(false);

  const handleRoomSelection = useCallback((room: RoomWithPackage) => {
    setSelectedControlRoom(room);
  }, []);

  const handleInternalRoomSelection = useCallback(
    (internalRoomId: string, selected: boolean) => {
      const update = new Set([...selectedInternalRooms]);
      if (!selected) {
        update.delete(internalRoomId);
      } else {
        update.add(internalRoomId);
      }
      setSelectedInternalRooms(update);
    },
    [selectedInternalRooms],
  );

  // Used in updating both the update and remove payloads
  const addToUpdateOrRemovePayload = useCallback(
    (payload: RoomMappingPayload, roomId, mappingsToAdd): RoomMappingPayload => {
      const newPayload = [...payload];
      mappingsToAdd.forEach((internalRoomId: string) => {
        let newMappings = newPayload.find((mapping) => mapping.roomId === roomId);
        if (!newMappings) {
          newMappings = { roomId, internalRoomIds: [internalRoomId] };
          newPayload.push(newMappings);
        } else {
          newMappings.internalRoomIds.push(internalRoomId);
        }
      });
      return newPayload;
    },
    [],
  );

  const handleUpdateMapping = useCallback(() => {
    if (!selectedControlRoom) {
      enqueueSnackbar('Please select a room from property rooms', { variant: 'error' });
      return;
    }

    if (Array.from(selectedInternalRooms.entries()).length === 0) {
      enqueueSnackbar('Please select at least one internal room', { variant: 'error' });
      return;
    }

    // update payload
    const newUpload = addToUpdateOrRemovePayload(updatePayload, selectedControlRoom.id, selectedInternalRooms);
    setUpdatePayload(newUpload);

    setAppliedButNotSavedRooms([
      ...appliedButNotSavedRooms,
      ...internalRooms.filter((room) => selectedInternalRooms.has(room.id)),
    ]);
    const newAlreadyMappedRooms = { ...alreadyMappedRooms };
    selectedInternalRooms.forEach((key) => {
      newAlreadyMappedRooms[key] = true;
    });

    setAlreadyMappedRooms(newAlreadyMappedRooms);
    // clear rooms from selection
    setSelectedInternalRooms(new Set());
    setSelectedControlRoom(undefined);
  }, [
    selectedControlRoom,
    selectedInternalRooms,
    addToUpdateOrRemovePayload,
    updatePayload,
    appliedButNotSavedRooms,
    internalRooms,
    alreadyMappedRooms,
    enqueueSnackbar,
  ]);

  const sortAndSavePropertyRooms = useCallback(
    (newPropertyRooms: Array<RoomWithPackage>) => {
      setPropertyRooms(newPropertyRooms.sort((a, b) => a.name.localeCompare(b.name)));
    },
    [setPropertyRooms],
  );

  const temporarilyRemoveFromPropertyRoomMappings = useCallback(
    (roomId: string, internalRoomId: string) => {
      const newPropertyRooms = [...propertyRooms];
      const roomIndex = newPropertyRooms.findIndex((room) => room.id === roomId);
      if (roomIndex === -1) {
        throw new Error(`Could not find room with id ${roomId}`);
      }
      const newMappedRooms = newPropertyRooms[roomIndex].mappedRooms.filter(
        (room) => room.internalRoomId !== internalRoomId,
      );
      newPropertyRooms[roomIndex] = { ...newPropertyRooms[roomIndex], mappedRooms: newMappedRooms };
      sortAndSavePropertyRooms(newPropertyRooms);
    },
    [propertyRooms, sortAndSavePropertyRooms],
  );

  const handleRemove = useCallback(
    (mappedRoom: ExtendedMappedInternalRoom) => {
      const remove = addToUpdateOrRemovePayload(removePayload, mappedRoom.roomId, [mappedRoom.internalRoomId]);
      setAppliedButNotSavedRooms(appliedButNotSavedRooms.filter((r) => r.id !== mappedRoom.internalRoomId));
      const newAlreadyMappedRooms = { ...alreadyMappedRooms };
      newAlreadyMappedRooms[mappedRoom.internalRoomId] = false;
      setAlreadyMappedRooms(newAlreadyMappedRooms);
      temporarilyRemoveFromPropertyRoomMappings(mappedRoom.roomId, mappedRoom.internalRoomId);
      setRemovePayload(remove);
    },
    [
      addToUpdateOrRemovePayload,
      removePayload,
      appliedButNotSavedRooms,
      alreadyMappedRooms,
      temporarilyRemoveFromPropertyRoomMappings,
    ],
  );

  const setRoomState = useCallback(
    (rooms: Array<RoomWithPackage>, unmappedInternalRooms: Array<InternalRoom>) => {
      sortAndSavePropertyRooms(rooms);
      const mappedInternalRoomIds: Array<string> = [];
      rooms.forEach((room) => {
        room.mappedRooms.forEach((mappedRoom) => {
          mappedInternalRoomIds.push(mappedRoom.internalRoomId);
        });
      });
      const alreadyMapped = {};
      mappedInternalRoomIds.forEach((mappedInternalRoomId) => {
        alreadyMapped[mappedInternalRoomId] = true;
      });
      unmappedInternalRooms.forEach((room) => {
        alreadyMapped[room.id] = false;
      });
      setAlreadyMappedRooms(alreadyMapped);
      setInternalRooms(unmappedInternalRooms);
    },
    [sortAndSavePropertyRooms],
  );

  const fetchRoomMappings = useCallback(async () => {
    const [mappingsResp, roomsResp] = await Promise.all([getRoomMappings(propertyId), listRooms({ propertyId })]);

    if (mappingsResp.message === 'failed') {
      return mappingsResp;
    }

    const packageMap: Record<string, boolean> = {};
    if (roomsResp.message === 'succeeded') {
      roomsResp.result.forEach((room: RoomResponse) => {
        packageMap[room.id] = room.hasActiveLpcPackage;
      });
    }

    const roomsWithPackage = mappingsResp.result.rooms.map((room) => ({
      ...room,
      hasActiveLpcPackage: packageMap[room.id],
    }));

    return { ...mappingsResp, result: { ...mappingsResp.result, rooms: roomsWithPackage } };
  }, [propertyId]);

  const onFetchSuccess = useCallback(
    (result: RoomMappingResponse) => {
      setRoomState(result.rooms, result.unmappedInternalRooms);
      setIsLoading(false);
    },
    [setRoomState],
  );

  const { fetchReqState } = useAccommodationServiceFetch<RoomMappingResponse>({
    fetchFn: fetchRoomMappings,
    onSuccess: onFetchSuccess,
  });

  const handleInternalPropertyFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setInternalPropertyFilter(event.target.value);
  }, []);

  const internalPropertyRoomIds = internalPropertyRooms.find(
    ({ internalProperty }) => internalProperty.id === internalPropertyFilter,
  )?.roomIds;

  const clearNewMappings = useCallback(
    (room: RoomWithPackage) => {
      const mappingsToClear = updatePayload.find((r) => r.roomId === room.id);
      const newAlreadyMappedRooms = { ...alreadyMappedRooms };
      mappingsToClear.internalRoomIds.forEach((internalRoomId) => {
        newAlreadyMappedRooms[internalRoomId] = false;
      });
      setAlreadyMappedRooms(newAlreadyMappedRooms);
      const otherMappings = updatePayload.filter((update) => update.roomId !== room.id);
      setUpdatePayload(otherMappings);
      // Any applied but not saved rooms that is not in the update payload should be cleared (only ones in the update payload should be kept)
      setAppliedButNotSavedRooms(
        appliedButNotSavedRooms.filter((r) => otherMappings.some((mappedRoom) => mappedRoom.roomId !== r.id)),
      );
    },
    [updatePayload, alreadyMappedRooms, appliedButNotSavedRooms],
  );

  const handleSave = useCallback(async () => {
    setCloseVerificationCommentsModal();
    setIsLoading(true);
    try {
      // if verifications then show verification comments dialog
      const [updateResponse, removeResponse, verifyResponse] = await Promise.all([
        updatePayload.length > 0 && postRoomMappings(propertyId, updatePayload),
        removePayload.length > 0 && deleteRoomMappings(propertyId, removePayload),
        verifiedRoomMappingIds.size > 0 &&
          verifyMappedRooms(propertyId, {
            mappedRoomIds: [...verifiedRoomMappingIds],
            verified: true,
            verifiedComments: verificationComments,
          }),
      ]);
      if (
        updateResponse?.message === 'failed' ||
        removeResponse?.message === 'failed' ||
        verifyResponse?.message === 'failed'
      ) {
        throw new Error('Failed to save room mappings');
      }
      const { rooms, unmappedInternalRooms } = updateResponse?.result ??
        removeResponse?.result ??
        verifyResponse?.result ?? { rooms: [], unmappedInternalRooms: [] };
      setRoomState(rooms, unmappedInternalRooms);

      enqueueSnackbar('Successfully saved room mappings', { variant: 'success' });
      setUpdatePayload([]);
      setRemovePayload([]);
      setVerificationComments('');
      setVerifiedRoomMappingIds(new Set());
    } catch (error) {
      enqueueSnackbar('Failed to save room mappings', { variant: 'error' });
    }
    setIsLoading(false);
  }, [
    setCloseVerificationCommentsModal,
    updatePayload,
    propertyId,
    removePayload,
    verifiedRoomMappingIds,
    verificationComments,
    setRoomState,
    enqueueSnackbar,
  ]);

  const checkVerificationComments = useCallback(() => {
    if (Array.from(verifiedRoomMappingIds.values()).length > 0) {
      setOpenVerificationCommentsModal();
    } else {
      handleSave();
    }
  }, [verifiedRoomMappingIds, setOpenVerificationCommentsModal, handleSave]);

  const handleCreateAndMapRoom = useCallback(async () => {
    const confirmed = await showConfirmDialog({
      title: 'Create and map to new room?',
      description:
        'This will create a new room in the property and map the selected internal rooms to it. Are you sure you want to proceed?',
    });

    if (!confirmed) {
      return;
    }
    if (Array.from(selectedInternalRooms.entries()).length === 0) {
      enqueueSnackbar('Please select at least one internal room', { variant: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await createAndMapRoom(propertyId, Array.from(selectedInternalRooms.values()));
      const { rooms, unmappedInternalRooms } = response.result;
      setRoomState(rooms, unmappedInternalRooms);

      enqueueSnackbar('Successfully saved new room and mapping', { variant: 'success' });
      setSelectedInternalRooms(new Set());
    } catch (error) {
      enqueueSnackbar('Failed to save room mappings', { variant: 'error' });
    }
    setIsLoading(false);
  }, [showConfirmDialog, selectedInternalRooms, enqueueSnackbar, propertyId, setRoomState]);

  const handleCreateAndMapMultipleRooms = useCallback(async () => {
    const confirmed = await showConfirmDialog({
      title: 'Create and map to multiple new rooms?',
      description:
        'This will create a new room and mapping in the property for each internal room selected. Are you sure you want to proceed?',
    });

    if (!confirmed) {
      return;
    }
    if (Array.from(selectedInternalRooms.entries()).length === 0) {
      enqueueSnackbar('Please select at least one internal room', { variant: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      let response;
      for (const internalRoomId of Array.from(selectedInternalRooms.values())) {
        response = await createAndMapRoom(propertyId, [internalRoomId]); // the last response will contain all newly created rooms and mappings
      }
      const { rooms, unmappedInternalRooms } = response.result;
      setRoomState(rooms, unmappedInternalRooms);

      enqueueSnackbar('Successfully saved new room and mapping', { variant: 'success' });
      setSelectedInternalRooms(new Set());
    } catch (error) {
      enqueueSnackbar('Failed to save room mappings', { variant: 'error' });
      Sentry.captureException(error, {
        tags: {
          component: 'AccommodationPropertyRoomMappingSection',
          propertyId,
        },
      });
    }
    setIsLoading(false);
  }, [showConfirmDialog, selectedInternalRooms, enqueueSnackbar, propertyId, setRoomState]);

  const handleVerifyMappedRoom = useCallback(
    async (mappedRoom: MappedInternalRoom) => {
      const roomToUpdate = new Set([...verifiedRoomMappingIds]);
      roomToUpdate.add(mappedRoom.mappedRoomId);
      setVerifiedRoomMappingIds(roomToUpdate);
      let propertyRoomNowVerified;
      propertyRooms.forEach((room) => {
        room.mappedRooms.forEach((mr) => {
          if (mr.mappedRoomId === mappedRoom.mappedRoomId) {
            mr.verificationStatus = 'Approved';
            propertyRoomNowVerified = room;
          }
        });
      });
      const filteredRooms = [
        ...propertyRooms.filter((room) => room.id !== propertyRoomNowVerified.id),
        propertyRoomNowVerified,
      ];
      sortAndSavePropertyRooms(filteredRooms);
    },
    [propertyRooms, sortAndSavePropertyRooms, verifiedRoomMappingIds],
  );

  return (
    <>
      {fetchReqState.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {JSON.stringify(fetchReqState.error)}
        </Alert>
      )}
      <Container disableGutters>
        <Stack direction="row" spacing={2} mb={2} justifyContent="space-between">
          <Typography variant="h6" display="flex" justifyContent="space-evenly" width="100%">
            {updatePayload.length > 0 && (
              <Alert color="success">
                {updatePayload.length} {pluralize('change', updatePayload.length)} to apply
              </Alert>
            )}
            {removePayload.length > 0 && (
              <Alert color="error">
                {removePayload.length} {pluralize('change', removePayload.length)} to apply
              </Alert>
            )}
            {updatePayload.length === 0 && removePayload.length === 0 && 'Map Internal Rooms to Property Rooms'}
          </Typography>
          {(updatePayload.length > 0 ||
            removePayload.length > 0 ||
            Array.from(verifiedRoomMappingIds.values()).length > 0) && (
            <Box justifyContent="end">
              <Button variant="contained" onClick={checkVerificationComments}>
                Save
              </Button>
            </Box>
          )}
        </Stack>
        {isLoading && (
          <Box m={2}>
            <LinearProgress />
          </Box>
        )}
        <Stack direction="column" spacing={0.5} mb={2} maxWidth={480}>
          <Typography variant="caption" color="text.secondary">
            Internal property
          </Typography>
          <Select
            size="small"
            value={internalPropertyFilter}
            onChange={handleInternalPropertyFilterChange}
            disabled={isInternalPropertyRoomsLoading || isInternalPropertyRoomsError}
            displayEmpty
          >
            <MenuItem value="">All internal properties</MenuItem>
            {internalPropertyRooms.map(({ internalProperty }) => (
              <MenuItem key={internalProperty.id} value={internalProperty.id}>
                {internalProperty.name} ({titleCase(internalProperty.source)} {internalProperty.sourceId})
                {internalProperty.offers?.some((offer) => offer.isActive) && ' - Live'}
              </MenuItem>
            ))}
          </Select>
          {isInternalPropertyRoomsError && (
            <Typography variant="caption" color="error">
              Failed to load internal properties
            </Typography>
          )}
        </Stack>
        <Box display="grid" gridTemplateColumns="480px 1fr" gap={3}>
          {/* Container for unmapped Internal Rooms */}
          <Box position="relative">
            <UnmappedRooms
              selectedInternalRooms={selectedInternalRooms}
              internalRooms={internalRooms}
              alreadyMappedRooms={alreadyMappedRooms}
              appliedButNotSavedRooms={appliedButNotSavedRooms}
              handleInternalRoomSelection={handleInternalRoomSelection}
              internalPropertyRoomIds={internalPropertyRoomIds}
            />
          </Box>
          {/* Container for base Rooms with mappings */}
          <MappedRooms
            propertyRooms={propertyRooms}
            selectedControlRoom={selectedControlRoom}
            selectedInternalRooms={selectedInternalRooms}
            handleCreateAndMapRoom={handleCreateAndMapRoom}
            handleCreateAndMapMultipleRooms={handleCreateAndMapMultipleRooms}
            handleRoomSelection={handleRoomSelection}
            handleUpdateMapping={handleUpdateMapping}
            handleVerifyMappedRoom={handleVerifyMappedRoom}
            appliedButNotSavedRooms={appliedButNotSavedRooms}
            handleRemove={handleRemove}
            updatePayload={updatePayload}
            removePayload={removePayload}
            clearNewMappings={clearNewMappings}
            internalPropertyRoomIds={internalPropertyRoomIds}
          />
        </Box>
      </Container>
      <Dialog open={isVerificationCommentsModalOpen} onClose={() => setCloseVerificationCommentsModal()}>
        {isVerificationCommentsModalOpen && (
          <>
            <DialogTitle>
              You've verified {Array.from(verifiedRoomMappingIds.values()).length} room{' '}
              {pluralize('mappings', Array.from(verifiedRoomMappingIds.values()).length)}{' '}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                [Optional] Provide verification comments for the selected room mappings
              </Typography>
              <TextField
                sx={{ mt: 2 }}
                label="Verification comments"
                value={verificationComments}
                type="text"
                onChange={(e) => setVerificationComments(e.target.value)}
                fullWidth
              />
            </DialogContent>
            <Stack direction="row" justifyContent="end" p={3}>
              <Button onClick={handleSave} variant="outlined" color="primary">
                Continue
              </Button>
            </Stack>
          </>
        )}
      </Dialog>
    </>
  );
}

export default AccommodationPropertyRoomMappingSection;
